<script>
import Layout from '@layouts/main'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'

import estoqueService from '@/src/services/api-receituario/estoque'
import produtoService from '@/src/services/produto_estoque'
import { buildStockSummary, formatMovementDate } from './stock-helpers'

export default {
  components: { Layout, Loading },
  data() {
    return {
      isLoading: false,
      produtos: [],
      movimentos: [],
      resumoProdutos: [],
      form: this.getDefaultForm(),
      tiposMovimento: [
        { tipo_movimento: 'E', descricao: 'Entrada' },
        { tipo_movimento: 'S', descricao: 'Saida' },
      ],
    }
  },
  mounted() {
    this.carregarDados()
  },
  computed: {
    produtoAtual() {
      return (
        this.resumoProdutos.find(
          (item) => String(item.id_produto) === String(this.form.id_produto)
        ) || null
      )
    },
  },
  methods: {
    getDefaultForm() {
      return {
        tipo_movimento: 'E',
        id_produto: '',
        data: new Date().toISOString().slice(0, 10),
        disponivel: '',
        minimo: '',
        maximo: '',
        quantidade: '',
        n_nf: '',
        n_receita: '',
      }
    },
    async carregarDados() {
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true

      try {
        const [produtoResponse, estoqueResponse] = await Promise.all([
          produtoService.listar(token),
          estoqueService.listar(token),
        ])

        this.produtos = produtoResponse.data.produtos || []
        this.movimentos = estoqueResponse.data.estoques || []
        this.resumoProdutos = buildStockSummary(this.movimentos, this.produtos)

        if (this.$route.query.produto) {
          this.form.id_produto = this.$route.query.produto
          this.handleProductChange()
        }
      } catch (error) {
        this.$bvToast.toast(
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
            'Nao foi possivel carregar os dados do ajuste.',
          {
            variant: 'danger',
            solid: false,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          }
        )
      } finally {
        this.isLoading = false
      }
    },
    handleProductChange() {
      if (!this.produtoAtual) {
        return
      }

      this.form.disponivel = this.produtoAtual.disponivel
      this.form.minimo = this.produtoAtual.minimo
      this.form.maximo = this.produtoAtual.maximo
    },
    async salvarAjuste() {
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true

      try {
        await estoqueService.salvar(token, this.form)
        this.$bvToast.toast('Ajuste salvo com sucesso.', {
          variant: 'success',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
        this.form = this.getDefaultForm()
        await this.carregarDados()
      } catch (error) {
        this.$bvToast.toast(
          (error.response &&
            error.response.data &&
            (error.response.data.data || error.response.data.message)) ||
            'Nao foi possivel salvar o ajuste.',
          {
            variant: 'danger',
            solid: false,
            noCloseButton: true,
            toaster: 'b-toaster-top-center',
          }
        )
      } finally {
        this.isLoading = false
      }
    },
    formatDate(value) {
      return formatMovementDate(value)
    },
  },
}
</script>

<template>
  <Layout>
    <div class="stock-page-shell">
      <div class="stock-page-hero">
        <div>
          <span class="stock-page-eyebrow">Operacao</span>
          <h1 class="stock-page-title">Ajuste de Estoque</h1>
          <p class="stock-page-subtitle">
            Lance entradas ou saidas rapidamente usando o produto ja existente
            no cadastro principal.
          </p>
        </div>
        <div class="stock-page-actions">
          <button class="btn btn-outline-primary" @click="$router.push('/cadastros/produto')">
            Cadastro de produto
          </button>
          <button class="btn btn-light" @click="$router.push('/estoque/movimentacao')">
            Historico
          </button>
        </div>
      </div>

      <div class="stock-adjust-grid">
        <section class="stock-panel">
          <div class="stock-panel-head">
            <div>
              <h2>Novo ajuste</h2>
              <p>Use este formulario para lancamentos rapidos de entrada ou saida.</p>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Tipo de movimentacao">
                <v-select
                  class="style-select"
                  :options="tiposMovimento"
                  label="descricao"
                  :reduce="(item) => item.tipo_movimento"
                  v-model="form.tipo_movimento"
                />
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Produto">
                <v-select
                  class="style-select"
                  :options="produtos"
                  label="nome"
                  :reduce="(item) => item.id_produto"
                  v-model="form.id_produto"
                  @input="handleProductChange"
                />
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Data">
                <b-form-datepicker
                  v-model="form.data"
                  locale="pt-br"
                  :date-format-options="{
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  }"
                >
                  <template #button-content>
                    <b-icon icon="calendar" variant="primary" />
                  </template>
                </b-form-datepicker>
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Saldo informado">
                <b-form-input
                  v-model="form.disponivel"
                  type="number"
                  placeholder="Informe o saldo apos o ajuste"
                />
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Estoque minimo">
                <b-form-input v-model="form.minimo" type="number" />
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Estoque maximo">
                <b-form-input v-model="form.maximo" type="number" />
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Quantidade embalagens">
                <b-form-input v-model="form.quantidade" type="number" />
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="NF">
                <b-form-input v-model="form.n_nf" type="text" />
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Nº receituario">
                <b-form-input v-model="form.n_receita" type="text" />
              </b-form-group>
            </div>
          </div>

          <div class="stock-form-actions">
            <button class="btn btn-light" @click="form = getDefaultForm()">
              Limpar
            </button>
            <button class="btn btn-webreceita" @click="salvarAjuste">
              Salvar ajuste
            </button>
          </div>
        </section>

        <aside class="stock-side-card">
          <div class="stock-panel-head">
            <div>
              <h2>Contexto do produto</h2>
              <p>Resumo rapido para ajustar com mais seguranca.</p>
            </div>
          </div>

          <div v-if="produtoAtual" class="stock-context">
            <div class="context-line">
              <span>Produto</span>
              <strong>{{ produtoAtual.nome }}</strong>
            </div>
            <div class="context-grid">
              <div>
                <span>Atual</span>
                <strong>{{ produtoAtual.disponivel }}</strong>
              </div>
              <div>
                <span>Minimo</span>
                <strong>{{ produtoAtual.minimo }}</strong>
              </div>
              <div>
                <span>Maximo</span>
                <strong>{{ produtoAtual.maximo }}</strong>
              </div>
              <div>
                <span>Ultima movimentacao</span>
                <strong>{{ formatDate(produtoAtual.ultima_movimentacao) }}</strong>
              </div>
            </div>
          </div>
          <div v-else class="stock-empty-side">
            Selecione um produto para carregar o contexto atual de estoque.
          </div>
        </aside>
      </div>

      <b-overlay :show="isLoading" no-wrap />
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
.stock-page-shell {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.stock-page-hero,
.stock-panel,
.stock-side-card {
  background: #fff;
  border: 1px solid rgba(0, 142, 117, 0.14);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(15, 61, 52, 0.06);
}

.stock-page-hero {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 26px 28px;
}

.stock-page-eyebrow {
  display: inline-block;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #008e75;
}

.stock-page-title {
  margin: 0 0 8px;
  font-size: 32px;
  color: #11493f;
}

.stock-page-subtitle {
  margin: 0;
  color: #5f7a74;
}

.stock-page-actions {
  display: flex;
  gap: 12px;
}

.stock-adjust-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.85fr);
  gap: 20px;
}

.stock-panel,
.stock-side-card {
  padding: 24px;
}

.stock-panel-head {
  margin-bottom: 18px;
}

.stock-panel-head h2 {
  margin: 0 0 6px;
  font-size: 21px;
  color: #11493f;
}

.stock-panel-head p {
  margin: 0;
  color: #69847f;
}

.stock-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 10px;
}

.stock-context {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.context-line,
.context-grid div {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 16px;
  background: #f7fbfa;
  border: 1px solid rgba(0, 142, 117, 0.1);
}

.context-line span,
.context-grid span {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: #6e8782;
}

.context-line strong,
.context-grid strong {
  font-size: 17px;
  color: #11493f;
}

.context-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.stock-empty-side {
  padding: 30px 16px;
  border-radius: 16px;
  background: #f7fbfa;
  color: #6b8480;
}

@media (max-width: 991px) {
  .stock-page-hero,
  .stock-adjust-grid {
    grid-template-columns: 1fr;
    display: grid;
  }
}

@media (max-width: 767px) {
  .context-grid {
    grid-template-columns: 1fr;
  }
}
</style>
